import React, { useState } from 'react';
import { RefreshCw, Plus, Trash2, FileText, ChevronLeft, ChevronRight, Archive } from 'lucide-react';

interface FileListProps {
  files: string[];
  selectedFile: string | null;
  onFileSelect: (filename: string) => void;
  onRefresh: () => void;
  onCreateFile: (filename: string, content: any) => void;
  onDeleteFile: (filename: string) => void;
  loading: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenBackups?: () => void;
}

export const FileList: React.FC<FileListProps> = ({
  files,
  selectedFile,
  onFileSelect,
  onRefresh,
  onCreateFile,
  onDeleteFile,
  loading,
  isCollapsed = false,
  onToggleCollapse,
  onOpenBackups,
}) => {
  const [newFileName, setNewFileName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const handleCreateFile = () => {
    if (newFileName.trim()) {
      const filename = newFileName.endsWith('.json') ? newFileName : `${newFileName}.json`;
      onCreateFile(filename, {});
      setNewFileName('');
      setShowCreateForm(false);
    }
  };

  const handleDeleteFile = (filename: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`¿Estás seguro de que quieres eliminar ${filename}?`)) {
      onDeleteFile(filename);
    }
  };

  return (
    <div className={`file-list-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && (
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Archivos JSON</h2>
        )}
        <div className="sidebar-actions">
          {!isCollapsed && (
            <>
              <button
                className="btn btn-secondary"
                onClick={onRefresh}
                disabled={loading}
                style={{ padding: '8px' }}
                title="Actualizar lista"
              >
                <RefreshCw size={16} />
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateForm(!showCreateForm)}
                style={{ padding: '8px' }}
                title="Crear nuevo archivo"
              >
                <Plus size={16} />
              </button>
              {onOpenBackups && (
                <button
                  className="btn btn-secondary"
                  onClick={onOpenBackups}
                  style={{ padding: '8px' }}
                  title="Ver backups y versiones anteriores"
                >
                  <Archive size={16} />
                </button>
              )}
            </>
          )}
          {onToggleCollapse && (
            <button
              className="btn btn-secondary collapse-toggle"
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Expandir panel' : 'Colapsar panel'}
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          )}
        </div>
      </div>

      {!isCollapsed && showCreateForm && (
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Crear nuevo archivo</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              className="input"
              placeholder="nombre-del-archivo.json"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFile()}
            />
            <button className="btn btn-success" onClick={handleCreateFile}>
              Crear
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowCreateForm(false);
                setNewFileName('');
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {!isCollapsed && (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No hay archivos JSON disponibles</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              Crea un nuevo archivo para comenzar
            </p>
          </div>
        ) : (
          files.map((filename) => (
            <div
              key={filename}
              onClick={() => onFileSelect(filename)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                marginBottom: '8px',
                backgroundColor: selectedFile === filename ? '#eff6ff' : '#f8fafc',
                border: selectedFile === filename ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (selectedFile !== filename) {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFile !== filename) {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={16} color="#6b7280" />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>{filename}</span>
              </div>
              <button
                className="btn btn-danger"
                onClick={(e) => handleDeleteFile(filename, e)}
                disabled={loading}
                style={{ padding: '4px 8px', fontSize: '12px' }}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
        </div>
      )}

      {isCollapsed && (
        <div className="collapsed-indicator">
          <div className="collapsed-files-count">
            {files.length}
          </div>
          <div className="collapsed-selected-file">
            {selectedFile ? (
              <div className="selected-file-indicator" title={selectedFile}>
                <FileText size={16} />
              </div>
            ) : (
              <div className="no-selection-indicator" title="Ningún archivo seleccionado">
                <FileText size={16} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

